import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch, useSelector } from 'react-redux';

import { clearSuggestionsTopic, getSuggestionsTopic } from '@/store/topics/actions';

import { selectTopics } from '@/store/topics/selectors';

import { Divider, Empty, Flex, Spin, Typography } from 'antd';

import RelatedMaterialCard from '../RelatedMaterialCard';

import Utils from '@/Utils';
import PropTypes from 'prop-types';

import css from './RelatedMaterials.module.scss';

const { Title } = Typography;

const RelatedMaterials = ({ currentTopic }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('relatedMaterials');
  const { suggestionsTopic, isLoadingSugg, suggestionsPagination } = useSelector(selectTopics);

  const isOnline = () => Utils.isOnline();

  const tagIds = useMemo(
    () => (Array.isArray(currentTopic?.tags) ? currentTopic?.tags?.map(tag => tag?.id) : []),
    [currentTopic]
  );
  const skillIds = useMemo(
    () => (Array.isArray(currentTopic?.skills) ? currentTopic?.skills?.map(skill => skill?.id) : []),
    [currentTopic]
  );
  const hasMore =
    suggestionsPagination?.total > (suggestionsPagination?.page + 1) * suggestionsPagination?.page_size &&
    !isLoadingSugg;

  const isEmpty = useMemo(
    () =>
      Array.isArray(currentTopic?.skills) &&
      currentTopic?.skills.length === 0 &&
      Array.isArray(currentTopic?.tags) &&
      currentTopic?.tags.length === 0,
    [currentTopic]
  );

  useEffect(() => {
    if (!isEmpty && isOnline()) {
      const initData = {
        size: 20,
        skillIds: skillIds,
        tagIds: tagIds,
        page: 0,
      };
      dispatch(clearSuggestionsTopic());
      dispatch(getSuggestionsTopic(initData));
    }
  }, [currentTopic]);

  const loadMoreTopics = page => {
    const sendData = {
      size: 20,
      skillIds: skillIds,
      tagIds: tagIds,
      page: page - 1,
    };
    dispatch(getSuggestionsTopic(sendData));
  };

  // Убираем дубликаты материалов
  const correctTopics = useMemo(
    () => suggestionsTopic?.filter?.(topic => topic?.id !== currentTopic?.id),
    [suggestionsTopic]
  );

  const renderRelatedCards = useMemo(
    () => correctTopics?.map(topic => <RelatedMaterialCard key={topic?.id} material={topic} />),
    [suggestionsTopic]
  );

  const renderContent = useMemo(() => {
    switch (true) {
      case isLoadingSugg && correctTopics?.length === 0:
        return <Spin className={css['RelatedMaterials-spin']} />;

      case !isEmpty && correctTopics?.length !== 0:
        return (
          <InfiniteScroll
            pageStart={suggestionsPagination?.page + 1}
            loadMore={loadMoreTopics}
            hasMore={hasMore}
            initialLoad={false}
            threshold={300}
            useWindow={false}
            loader={isLoadingSugg && <RelatedMaterialCard.Skeleton />}
          >
            <Flex vertical gap={6}>
              {renderRelatedCards}
            </Flex>
          </InfiniteScroll>
        );

      case isEmpty || correctTopics?.length === 0:
        return <Empty description={t('uniqueTopic')} />;

      default:
        break;
    }
  }, [renderRelatedCards, isLoadingSugg]);

  useEffect(() => {
    return () => {
      dispatch(clearSuggestionsTopic());
    };
  }, []);

  return (
    <div className={css['RelatedMaterials']}>
      <Title className={css['RelatedMaterials-title']} level={2}>
        {t('title')}
      </Title>
      <span className={css['RelatedMaterials-subTitle']}>{t('subTitle')}</span>
      <div className={css['RelatedMaterials-container']}>{renderContent}</div>
    </div>
  );
};

RelatedMaterials.propTypes = {
  currentTopic: PropTypes.object,
};

export default RelatedMaterials;
